import { useCallback, useEffect } from 'react';
import { ACTIONS } from 'src/constants/common';
import { type Actions } from 'src/context/store/store-reducer';
import { useWidgetDataRuntime } from 'src/context/widget-data-runtime';
import { type ExperimentParams } from 'src/types/experiment';
import { getEntityId } from 'src/utils/get-entity-id';

const clearPayloadFromEmptyValues = <T>(rest) => {
  return Object.entries(rest).reduce<T>((acc, [key, value]) => {
    if (value !== '') {
      return {
        ...acc,
        [key]: value,
      };
    }

    return acc;
  }, {} as T);
};

export const useWidgetData = ({
  dispatch,
  ...rest
}: ExperimentParams & {
  amount?: number;
  dispatch: React.Dispatch<Actions>;
  loading: boolean;
}) => {
  const widgetDataRuntime = useWidgetDataRuntime();
  const { publicKey, merchantCode, currency, amount } =
    clearPayloadFromEmptyValues<typeof rest>(rest);
  const normalizedMerchantCode =
    merchantCode !== undefined ? String(merchantCode) : undefined;

  const getData = useCallback(
    () =>
      widgetDataRuntime.load({
        ab: {
          entityId: getEntityId(),
          entity: 'widgets_customer',
          platform: 'web',
          filters: {
            merchantId: publicKey,
            merchantCode: normalizedMerchantCode,
            currency,
          },
        },
        publicKey,
        merchantCode: normalizedMerchantCode,
        currency,
        amount,
        /**
         * `true` makes the backend drop the AB block from the response — no
         * experiments, and no feature toggles built on them. Details in
         * CS_API_http_getWidgetData.
         */
        webview: false,
      }),
    [
      publicKey,
      normalizedMerchantCode,
      currency,
      amount,
      widgetDataRuntime,
    ]
  );

  useEffect(() => {
    let isCurrentRequest = true;

    getData().then((data) => {
      if (isCurrentRequest) {
        dispatch({
          type: ACTIONS.GET_WIDGET_DATA,
          payload: data,
        });
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [dispatch, getData]);
};
